/**
 * Video Studio generation modes (Text / Image / Video to Video).
 */
import {
  ImageOutlined,
  LibraryMusicOutlined,
  SyncOutlined,
  TextFieldsOutlined,
  VideoFileOutlined,
} from "@mui/icons-material";
import { DURATION_OPTIONS } from "./durationOptions";
import {
  normalizeCategoryId,
  STUDIO_GENERATION_MODE_IDS,
} from "./studioGenerationMode";

/** @typedef {'best'|'alt'|'budget'} ModelTier */

/**
 * @typedef {Object} VideoStudioModel
 * @property {string} id
 * @property {string} label
 * @property {ModelTier} tier
 */

/** @type {Record<string, VideoStudioModel>} */
export const POYO_MODELS = {
  "omni-flash": { id: "omni-flash", label: "Omni Flash", tier: "best" },
  "seedance-2": { id: "seedance-2", label: "Seedance 2", tier: "best" },
  "seedance-2-fast": { id: "seedance-2-fast", label: "Seedance 2 Fast", tier: "alt" },
  "kling-3.0": { id: "kling-3.0", label: "Kling 3.0", tier: "best" },
  "kling-o3-standard": { id: "kling-o3-standard", label: "Kling O3/Standard", tier: "alt" },
  "kling-o3-pro": { id: "kling-o3-pro", label: "Kling O3/Pro", tier: "alt" },
  "kling-o3-4k": { id: "kling-o3-4k", label: "Kling O3/4K", tier: "alt" },
  "kling-avatar-2.0": { id: "kling-avatar-2.0", label: "Kling Avatar 2.0", tier: "best" },
  "kling-3.0-motion-control": {
    id: "kling-3.0-motion-control",
    label: "Kling 3.0 Motion Control",
    tier: "best",
  },
  "veo-3.1": { id: "veo-3.1", label: "Veo 3.1", tier: "alt" },
  "hailuo-2.3": { id: "hailuo-2.3", label: "Hailuo 2.3", tier: "budget" },
  "sora-2": { id: "sora-2", label: "Sora 2", tier: "alt" },
  "sora-2-pro": { id: "sora-2-pro", label: "Sora 2 Pro", tier: "best" },
  "runway-gen-4.5": { id: "runway-gen-4.5", label: "Runway Gen 4.5", tier: "alt" },
  "wan-2.7": { id: "wan-2.7", label: "Wan 2.7", tier: "budget" },
  "grok-imagine": { id: "grok-imagine", label: "Grok Imagine", tier: "best" },
  "grok-imagine-video-1.5": {
    id: "grok-imagine-video-1.5",
    label: "Grok Imagine Video 1.5",
    tier: "alt",
  },
};

function modelsFromIds(ids) {
  return ids.map((id) => POYO_MODELS[id]).filter(Boolean);
}

/** Core Kling set shared across most modes. */
const KLING_CORE_IDS = ["kling-3.0", "kling-o3-standard", "kling-o3-pro"];

/** Text → video: broadest catalog. */
const TEXT_TO_VIDEO_MODEL_IDS = [
  "omni-flash",
  "seedance-2",
  "seedance-2-fast",
  "grok-imagine",
  ...KLING_CORE_IDS,
  "kling-o3-4k",
  "veo-3.1",
  "sora-2",
  "sora-2-pro",
  "runway-gen-4.5",
  "wan-2.7",
  "hailuo-2.3",
];

/** Image → video: models that take start/reference frames well. */
const IMAGE_TO_VIDEO_MODEL_IDS = [
  "omni-flash",
  "seedance-2",
  "seedance-2-fast",
  "grok-imagine",
  "grok-imagine-video-1.5",
  ...KLING_CORE_IDS,
  "veo-3.1",
  "hailuo-2.3",
  "wan-2.7",
  "runway-gen-4.5",
];

/** Video → video: reference-clip capable models. */
const VIDEO_TO_VIDEO_MODEL_IDS = [
  "omni-flash",
  "seedance-2",
  "seedance-2-fast",
  "kling-o3-standard",
  "kling-o3-pro",
  "kling-3.0",
  "wan-2.7",
];

/** Music → video: models that accept driving audio. */
const MUSIC_TO_VIDEO_MODEL_IDS = [
  "seedance-2",
  "seedance-2-fast",
  ...KLING_CORE_IDS,
  "kling-avatar-2.0",
  "veo-3.1",
];

/** @typedef {'text-to-video'|'image-to-video'|'video-to-video'} VideoStudioCategoryId */

/**
 * @typedef {Object} VideoStudioCategory
 * @property {VideoStudioCategoryId} id
 * @property {string} labelKey
 * @property {string} fallback
 * @property {string} descKey
 * @property {string} descFallback
 * @property {string} noteKey
 * @property {string} noteFallback
 * @property {import('@mui/icons-material').SvgIconComponent} icon
 * @property {string[]} modelIds
 * @property {string} defaultModelId
 * @property {boolean} ready
 */

/** Display labels for Video Studio modes (library, filters). */
export const VIDEO_STUDIO_CATEGORY_LABELS = {
  "text-to-video": "Text to Video",
  "image-to-video": "Image to Video",
  "video-to-video": "Video to Video",
  "music-to-video": "Music to Video",
  "motion-sync": "Motion Sync",
  clipping: "AI Clipping",
  // Legacy
  "product-in-hand": "Text to Video",
  "ugc-spokesperson": "UGC Ads",
  cinematic: "Product Showcase",
  "multi-shot": "Multi-Shot",
  ecommerce: "E-Commerce",
};

const SHARED_MODEL_IDS = KLING_CORE_IDS;

/** @type {VideoStudioCategory[]} */
export const VIDEO_STUDIO_CATEGORIES = [
  {
    id: "text-to-video",
    labelKey: "videoStudioCatTextToVideo",
    fallback: VIDEO_STUDIO_CATEGORY_LABELS["text-to-video"],
    descKey: "videoStudioCatTextToVideoDesc",
    descFallback: "Describe your scene in words — no uploads required.",
    noteKey: "videoStudioNoteTextToVideo",
    noteFallback:
      "Omni Flash and Seedance for top multimodal quality. Kling for rich motion and native sound. Veo, Sora, Runway, Wan, and Hailuo for alternate looks and budgets.",
    icon: TextFieldsOutlined,
    modelIds: TEXT_TO_VIDEO_MODEL_IDS,
    defaultModelId: "omni-flash",
    ready: true,
  },
  {
    id: "image-to-video",
    labelKey: "videoStudioCatImageToVideo",
    fallback: VIDEO_STUDIO_CATEGORY_LABELS["image-to-video"],
    descKey: "videoStudioCatImageToVideoDesc",
    descFallback: "Upload a still image and animate it with your prompt.",
    noteKey: "videoStudioNoteImageToVideo",
    noteFallback:
      "Use a clear product or scene photo. Omni Flash, Seedance, Kling, Veo, Hailuo, Wan, and Runway support image-driven motion.",
    icon: ImageOutlined,
    modelIds: IMAGE_TO_VIDEO_MODEL_IDS,
    defaultModelId: "omni-flash",
    ready: true,
  },
  {
    id: "video-to-video",
    labelKey: "videoStudioCatVideoToVideo",
    fallback: VIDEO_STUDIO_CATEGORY_LABELS["video-to-video"],
    descKey: "videoStudioCatVideoToVideoDesc",
    descFallback: "Upload a reference clip and guide the output with your prompt.",
    noteKey: "videoStudioNoteVideoToVideo",
    noteFallback:
      "Provide an MP4, WebM, or MOV reference. Omni Flash, Seedance, Kling O3, and Wan handle reference-video restyles best.",
    icon: VideoFileOutlined,
    modelIds: VIDEO_TO_VIDEO_MODEL_IDS,
    defaultModelId: "omni-flash",
    ready: true,
  },
  {
    id: "music-to-video",
    labelKey: "videoStudioCatMusicToVideo",
    fallback: VIDEO_STUDIO_CATEGORY_LABELS["music-to-video"],
    descKey: "videoStudioCatMusicToVideoDesc",
    descFallback: "Upload a music track and generate visuals that follow the audio.",
    noteKey: "videoStudioNoteMusicToVideo",
    noteFallback:
      "Provide a clean MP3 or WAV. Seedance and Kling sync visuals to audio; Avatar 2.0 is ideal for talking-head + soundtrack.",
    icon: LibraryMusicOutlined,
    modelIds: MUSIC_TO_VIDEO_MODEL_IDS,
    defaultModelId: "seedance-2",
    ready: true,
  },
  {
    id: "motion-sync",
    labelKey: "videoStudioCatMotionSync",
    fallback: VIDEO_STUDIO_CATEGORY_LABELS["motion-sync"],
    descKey: "videoStudioCatMotionSyncDesc",
    descFallback: "Transfer motion from a reference video onto your character image.",
    noteKey: "videoStudioNoteMotionSync",
    noteFallback:
      "Upload one portrait image and one motion clip. Kling 3.0 Motion Control maps the action from the video onto your character.",
    icon: SyncOutlined,
    modelIds: ["kling-3.0-motion-control"],
    defaultModelId: "kling-3.0-motion-control",
    ready: true,
  },
];

export { DURATION_OPTIONS };

/** Filter / dropdown options for Video Studio generation modes. */
export const VIDEO_STUDIO_USE_CASE_FILTER_OPTIONS = STUDIO_GENERATION_MODE_IDS.map((id) => ({
  id,
  label: VIDEO_STUDIO_CATEGORY_LABELS[id],
}));

export { STUDIO_GENERATION_MODE_IDS };

export function categoryDisplayLabel(categoryId, lang) {
  const normalized = normalizeCategoryId(categoryId);
  const cat = VIDEO_STUDIO_CATEGORIES.find((c) => c.id === normalized);
  if (!cat) return VIDEO_STUDIO_CATEGORY_LABELS[normalized] || categoryId;
  return lang?.[cat.labelKey] || cat.fallback;
}

export const MODEL_TIER_LABELS = {
  best: { key: "videoStudioTierBest", fallback: "Top pick" },
  alt: { key: "videoStudioTierAlt", fallback: "Alternative" },
  budget: { key: "videoStudioTierBudget", fallback: "Budget / fast" },
};

export const ASPECT_RATIO_OPTIONS = ["16:9", "9:16", "1:1", "4:3", "3:4"];
/** Grok Imagine text-to-video aspect ratios (PoYo). */
export const GROK_ASPECT_RATIO_OPTIONS = ["16:9", "9:16", "1:1", "2:3", "3:2"];
export const RESOLUTION_OPTIONS = ["480p", "720p", "1080p", "4K"];
export const GROK_MODE_OPTIONS = ["normal", "fun", "spicy"];
export const GROK_VIDEO_15_RESOLUTIONS = ["480p", "720p"];

/** @param {VideoStudioCategory | null | undefined} category */
export function modelsForCategory(category) {
  return modelsFromIds(category?.modelIds || []);
}

export function categoryModelNote(category, lang) {
  if (!category) return "";
  return lang?.[category.noteKey] || category.noteFallback;
}

export function estimateCredits({
  duration,
  resolution,
  generateAudio,
  model,
  avatarTier,
  soraStoryboard,
}) {
  const id = String(model || "");
  const resMult = { "480p": 1, "720p": 1.25, "1080p": 1.75, "4k": 3, "4K": 3 };
  if (id.includes("kling-avatar")) {
    const audioSec = Math.min(60, Math.max(2, Number(duration) || 30));
    const tierMult = avatarTier === "pro" ? 1.35 : 1;
    return Math.max(1, Math.ceil(6 * audioSec * tierMult));
  }
  if (id.includes("motion-control")) {
    const resMult = { "720p": 1, "1080p": 1.5 };
    const r = resMult[resolution] ?? 1;
    const d = Number(duration) || 10;
    return Math.max(1, Math.ceil(8 * d * r));
  }
  if (id.includes("veo")) {
    const resMult = { "720p": 1, "1080p": 1.4, "4k": 2.5, "4K": 2.5 };
    return Math.max(1, Math.ceil(18 * (resMult[resolution] ?? 1)));
  }
  if (id.includes("omni-flash") || id.includes("omni")) {
    const d = Number(duration) || 6;
    const resMult = { "720p": 1, "1080p": 1, "4k": 2, "4K": 2 };
    return Math.max(1, Math.ceil((d <= 4 ? 20 : d <= 6 ? 25 : d <= 8 ? 33 : 37) * (resMult[resolution] ?? 1)));
  }
  if (id.includes("seedance")) {
    const d = Number(duration) || 8;
    const fast = id.includes("fast");
    const perSec = fast ? 28 : 40;
    const r = resMult[resolution] ?? 1;
    return Math.max(1, Math.ceil(perSec * d * r * 0.35));
  }
  if (id.includes("grok-imagine-video") || id.includes("grok-imagine-video-1.5")) {
    const d = Number(duration) || 6;
    const r = resolution === "480p" ? 0.85 : 1;
    return Math.max(1, Math.ceil(22 * d * r * 0.2));
  }
  if (id.includes("grok")) {
    const d = Number(duration) || 6;
    return Math.max(1, Math.ceil(d >= 10 ? 32 : 22));
  }
  if (id.includes("hailuo")) {
    const d = Number(duration) >= 10 ? 1.6 : 1;
    const r = resolution === "1080p" ? 1.5 : 1;
    return Math.max(1, Math.ceil(10 * d * r));
  }
  if (id.includes("sora")) {
    const d = Number(duration) || 15;
    const mult = id.includes("pro") ? (d >= 25 ? 2.8 : 2) : 1.5;
    const sb = soraStoryboard ? 1.15 : 1;
    return Math.max(1, Math.ceil(28 * mult * sb));
  }
  if (id.includes("kling-o3-4k")) {
    return Math.max(1, Math.ceil(24 * (resMult[resolution] ?? 1)));
  }
  if (id.includes("runway")) {
    const d = Number(duration) || 5;
    const mult = d >= 10 ? 1.75 : 1;
    return Math.max(1, Math.ceil(14 * mult));
  }
  const durationMult = { 3: 1, 5: 1.4, 10: 2.2, 15: 3.2 };
  const base = 12;
  const d = durationMult[duration] ?? 1;
  const r = resMult[resolution] ?? 1;
  const audio = generateAudio ? 1.1 : 1;
  return Math.max(1, Math.ceil(base * d * r * audio));
}

export function getCategoryById(id) {
  const normalized = normalizeCategoryId(id);
  return VIDEO_STUDIO_CATEGORIES.find((c) => c.id === normalized) || VIDEO_STUDIO_CATEGORIES[0];
}
